"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Navbar from "../../components/account/Navbar";
import { CloudDownload } from "lucide-react";
import PetProfileView from "../../components/petProfiles/PetProfileView";
import { petAPI } from "../../services/api";
import Footer from "../../components/account/Footer";
import "./PetStyles.css";
// Footer Component (same as other pages)
const FooterOld = () => (
  <footer
    style={{
      background: "#FFFFFF",
      borderTop: "1px solid #E5E7EB",
      padding: "40px 60px",
      marginTop: "80px",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          fontFamily: '"Red Hat Display", sans-serif',
          fontSize: "14px",
          color: "#6B7280",
        }}
      >
        All rights reserved to:{" "}
        <span style={{ fontWeight: 600, color: "#374151" }}>Rubix</span>{" "}
        ©2025
      </div>


    </div>
  </footer>
);

// Main Pet Profile Component
const OthersPetProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userType, petId } = useParams();

  // Check if user came from profile page and which tab
  const fromProfile = location.state?.fromProfile || false;
  const profileIdFromState = location.state?.profileId || null;

  // Force activeTab to always be "Pet Profile" for view-only mode
  const activeTab = "Pet Profile";
  // Removed inline editing states since we're redirecting to edit page
  const [petData, setPetData] = useState(null);
  // Removed editablePetData state since we're redirecting to edit page
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [petImagePreview, setPetImagePreview] = useState(null);
  const [petImageFile, setPetImageFile] = useState(null);
  const [ownerId, setOwnerId] = useState(null);
  const [ownerUserType, setOwnerUserType] = useState(null);

  // Fetch owner's user type for color scheme
  const fetchOwnerUserType = async (userId) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === "success" && result.data) {
          // The user data is directly in result.data, not result.data.user
          const userData = result.data;

          // Check multiple possible locations for user_type
          const userType = userData.user_type ||
            userData.userType ||
            userData.type ||
            userData.user?.user_type ||
            userData.user?.userType ||
            null;

          setOwnerUserType(userType);
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to fetch owner user type:', response.status, response.statusText);
        }
      }
    } catch (err) {
      console.error("Error fetching owner user type:", err);
    }
  };

  useEffect(() => {
    const fetchPetData = async () => {
      if (!petId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const response = await petAPI.getOtherPet(petId);

        if (response.data && response.data.status === "success" && response.data.data) {
          const pet = response.data.data;
          setPetData(pet);
          setPetImagePreview(pet.primary_image_url || pet.profile_image_url || pet.image || null);
          // Extract owner ID for navigation - check multiple possible fields
          const ownerIdValue = pet.owner_id ||
            pet.user_id ||
            null;
          setOwnerId(ownerIdValue);

          // Check if pet data includes owner information with user_type
          if (pet.owner?.user_type) {
            setOwnerUserType(pet.owner.user_type);
          } else if (ownerIdValue) {
            // Fallback: Fetch owner's user type for color scheme if not in pet data
            fetchOwnerUserType(ownerIdValue);
          }
        } else {
          // Extract error message from result object or use default message
          const errorMessage = response?.data?.message || response?.data?.error || response?.message || "Failed to fetch pet data";
          setError(errorMessage);
          console.error("Failed to fetch pet:", response);
        }
      } catch (err) {
        console.error("Error fetching pet data:", err);
        setError(err?.response?.data?.message || err?.message || "Failed to fetch pet data");
      } finally {
        setLoading(false);
      }
    };

    fetchPetData();
  }, [petId]);


  // Removed handleImageChange and handleInputChange since we're redirecting to edit page
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
  };
  // View-only mode - no editing allowed
  const handleEditSection = () => {
    // Do nothing - view only
  };

  // Removed inline editing functions since we're redirecting to edit page

  // View-only mode - only show Pet Profile tab
  const renderTabContent = () => {
    return (
      <PetProfileView
        petData={petData}
        isOwner={false}
        ownerUserType={ownerUserType}
      />
    );
  };

  // Determine the back navigation path
  const getBackPath = () => {
    if (profileIdFromState) {
      return `/profile/${profileIdFromState}`;
    }
    if (ownerId) {
      return `/profile/${ownerId}`;
    }
    // Fallback: Try to get owner ID from petData if not set yet
    const currentOwnerId = petData?.owner_id ||
      petData?.user_id ||
      petData?.owner?.id ||
      petData?.owner?.user_id;
    if (currentOwnerId) {
      return `/profile/${currentOwnerId}`;
    }
    return -1; // Go back to previous page
  };

  const getBackLabel = () => {
    if (profileIdFromState || ownerId || petData?.owner_id || petData?.user_id || petData?.owner?.id) {
      return "Back to Profile";
    }
    return "Back";
  };

  if (loading) {
    return (
      <div className="petprofile-root petprofile-center-content">
        <div className="petprofile-loading-container">
          <div className="petprofile-spinner"></div>
          <h2 className="petprofile-loading-title">
            Loading Pet Profile...
          </h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="petprofile-root petprofile-center-content">
        <div className="petprofile-error-container">
          <h2 className="petprofile-error-title">
            Error Loading Pet
          </h2>
          <p className="petprofile-error-message">{error}</p>
          <div className="petprofile-error-buttons">
            <button
              onClick={() => window.location.reload()}
              className="petprofile-btn-primary"
            >
              Try Again
            </button>
            <button
              onClick={() => {
                const backPath = getBackPath();
                if (backPath === -1) {
                  navigate(-1);
                } else {
                  navigate(backPath);
                }
              }}
              className="petprofile-btn-secondary"
            >
              {getBackLabel()}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!petData) {
    return (
      <div className="petprofile-root petprofile-center-content">
        <div className="petprofile-error-container">
          <h2 className="petprofile-error-title">
            Pet Not Found
          </h2>
          <p className="petprofile-error-message">
            The pet you're looking for doesn't exist.
          </p>
          <button
            onClick={() => {
              const backPath = getBackPath();
              if (backPath === -1) {
                navigate(-1);
              } else {
                navigate(backPath);
              }
            }}
            className="petprofile-btn-primary"
          >
            {getBackLabel()}
          </button>
        </div>
      </div>
    );
  }

  // Removed renderField function since we're redirecting to edit page

  return (
    <div className="petprofile-root">
      <Navbar userType={userType} />

      <main className="petprofile-main">
        <div className="addpet-container">
          {/* Header Section with Back Button */}
          <div className="addpet-header">
            <button
              onClick={() => {
                const backPath = getBackPath();
                if (backPath === -1) {
                  navigate(-1);
                } else {
                  navigate(backPath);
                }
              }}
              className="addpet-back-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {getBackLabel()}
            </button>
          </div>

          {/* Pet Image */}
          <div className="petprofile-image-container">
            <div className="petprofile-image-wrapper">
              {petImagePreview && petImagePreview !== "/placeholder-pet.png" ? (
                <img
                  src={petImagePreview}
                  alt="Pet Preview"
                  className="petprofile-image"
                  onError={(e) => {
                    console.error("Image failed to load:", petImagePreview);
                    e.target.src = "/placeholder-pet.png";
                  }}
                />
              ) : (
                <CloudDownload size={60} color="#60A5FA" strokeWidth={1.5} />
              )}
            </div>
          </div>

          {/* View-only: Only show Pet Profile content, no tabs */}
          {renderTabContent()}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OthersPetProfile;
